import { memo, Suspense, useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { AxiosError } from "axios";
import { useReactToPrint } from "react-to-print";
import { TFunction } from "i18next";

import { ReactPortal } from "components/common/portal";
import { QuickPayPayment } from "components/pdf/QuickPayPayment";
import { Share } from "components/widgets/paymentActivity/share/share";
import { Dialog } from "components/common/dialog/Dialog";

import { useRootDispatch, useRootSelector } from "store";

import { useRequestDispatch } from "store/requestHooks";
import { SchedulePayments } from "store/schedulePayments/schedulePayments-state";
import {
  UpdateSchedulePayments,
  updateSchedulePayments,
} from "store/schedulePayments/schedulePayments-action";
import { FetchAutoPay, fetchAutoPay } from "store/payments/payments-action";
import { FetchReceipt, fetchReceipt } from "store/receipt/receipt-action";
import { useAddNotification } from "hooks";

import { extractError } from "utility";

import classes from "./TableActionIcons.module.scss";
import { ReceiptData } from "store/receipt/receipt-state";

interface TableActionDropdownProps {
  value?: any;
  data: any;
  t?: TFunction;
}

export const TableActionIcons: React.FC<TableActionDropdownProps> = memo(
  ({ data }) => {
    const [shareData, setShareData] = useState(null);
    const user = useRootSelector((state) => state.user.user);
    const [openShareDialog, setShareOpenDialog] = useState(false);
    const [printData, setPrintData] = useState<ReceiptData | undefined>(
      undefined
    );
    const componentRef = useRef();
    const dispatch = useRootDispatch();
    const requestDispatch = useRequestDispatch();
    const { t } = useTranslation(["common"]);
    const addNotification = useAddNotification();

    const handlePrint = useReactToPrint({
      content: () => (componentRef.current ? componentRef.current : null),
    });

    const [showConfirmDataObj, setShowConfirmDataObj] = useState(null);

    const iconClickHandler = useCallback(
      (rowData: any, type: string) => {
        try {
          if (type === "share") {
            setShareData(rowData);
            setShareOpenDialog(true);
          } else if (type === "download") {
            generateReceiptData(rowData.id);
          } else if (type === "cancel") {
            if (Object.hasOwn(rowData, "dataObj")) {
              setShowConfirmDataObj({
                ...rowData.dataObj,
                status: 1,
              });
            } else {
              setShowConfirmDataObj({
                ...rowData,
                status: 1,
              });
            }
          }
        } catch (error) {
          console.error("Error in table icon click handler:", error);
        }
      },
      // eslint-disable-next-line react-hooks/exhaustive-deps
      [user, dispatch]
    );

    const generateReceiptData = async (confirmationNumber: number) => {
      requestDispatch<FetchReceipt, number>(fetchReceipt, confirmationNumber)
        .unwrap()
        .then((res: ReceiptData) => {
          setPrintData(res);
          //TODO: hack need to resolve.
          setTimeout(() => {
            handlePrint();
          }, 10);
        })
        .catch((err: AxiosError) => {
          const errCode = extractError(err);
          addNotification({
            messages: errCode,
            type: "ERROR",
            closable: true,
          });
        });
    };

    const dialogActionHandler = () => {
      requestDispatch<UpdateSchedulePayments, SchedulePayments>(
        updateSchedulePayments,
        showConfirmDataObj!
      )
        .unwrap()
        .then(() => {
          requestDispatch<FetchAutoPay>(fetchAutoPay);
        })
        .catch((e: AxiosError) => {
          const errCode = extractError(e);
          addNotification({
            messages: errCode,
            type: "ERROR",
            closable: true,
          });
        });
    };

    return (
      <>
        <div key="share" className={classes.ShareDownload}>
          {data.schedulePayment ||
          (data.scheduledPaymentDate && data.status !== 1) ? (
            <ft-icon-button
              onClick={() => iconClickHandler(data, "cancel")}
              class={classes.ShareIcon}
              icon="highlight_off"
              aria-label="setting icon  for payment methods"
              title={t("common:cancel_label")}
            ></ft-icon-button>
          ) : (
            <>
              <ft-icon-button
                onClick={() => iconClickHandler(data, "download")}
                class={classes.DownloadIcon}
                icon="download"
                title={t("common:download")}
              ></ft-icon-button>
            </>
          )}
        </div>
        {showConfirmDataObj && (
          <ReactPortal wrapperId="delete_schedule">
            <Suspense fallback={<ft-screen-loader></ft-screen-loader>}>
              <Dialog
                dialogClosed={() => setShowConfirmDataObj(null)}
                type="warning"
                primaryActionHandler={dialogActionHandler}
                closeIcon
                primaryActionLabel={t("common:cancel")}
                showSecondaryAction={true}
                secondaryActionHandler={() => setShowConfirmDataObj(null)}
                secondaryActionLabel={t("common:close")}
              >
                {t("common:cancelConfirmationPopupText")}
              </Dialog>
            </Suspense>
          </ReactPortal>
        )}
        {openShareDialog ? (
          <ReactPortal wrapperId="payment-activity">
            <Suspense fallback={<ft-screen-loader></ft-screen-loader>}>
              <Share
                setOpenDialog={setShareOpenDialog}
                data={shareData}
              ></Share>
            </Suspense>
          </ReactPortal>
        ) : null}
        {printData && (
          <ReactPortal wrapperId="portalContainer">
            <div className={classes.PrintCss}>
              <Suspense fallback={<ft-screen-loader></ft-screen-loader>}>
                <QuickPayPayment
                  ref={componentRef}
                  paymentData={printData}
                ></QuickPayPayment>
              </Suspense>
            </div>
          </ReactPortal>
        )}
      </>
    );
  }
);
